import { ChangeDetectionStrategy, Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import {
    DataService,
    FormInputComponent,
    GetCollectionContentsQuery,
    GetProductVariantListSimpleQuery,
    GetProductVariantQuery,
    ModalService,
    RelationCustomFieldConfig,
    RelationSelectorDialogComponent,
    SharedModule,
} from '@vendure/admin-ui/core';
import { GET_PRODUCT_VARIANTS_BY_COLLECTION } from './relation-product-variant-input.graphql';

@Component({
    templateUrl: './relation-product-variant-input.component.html',
    styleUrls: ['./relation-product-variant-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],
})
export class RelationProductVariantInputComponent implements OnInit, FormInputComponent {
    @Input() readonly: boolean;
    @Input() formControl: UntypedFormControl;
    @Input() config: RelationCustomFieldConfig;
    @Input() collectionSlug: string;

    @ViewChild('selector') template: TemplateRef<any>;

    searchControl = new UntypedFormControl('');
    searchTerm$ = new Subject<string>();

    allVariants: any = [];
    filteredVariants$: Observable<typeof this.allVariants>;

    productVariant$: Observable<GetProductVariantQuery['productVariant'] | undefined>;

    constructor(
        private modalService: ModalService,
        private dataService: DataService,
    ) {}

    ngOnInit() {
        this.collectionSlug = this.config.ui?.config?.collectionSlug;

        this.productVariant$ = this.formControl.valueChanges.pipe(
            startWith(this.formControl.value),
            map(variant => variant?.id),
            distinctUntilChanged(),
            switchMap(id =>
                id
                    ? this.dataService.product
                          .getProductVariant(id)
                          .mapStream(data => data.productVariant || undefined)
                    : of(undefined),
            ),
        );

        this.dataService
            .query(GET_PRODUCT_VARIANTS_BY_COLLECTION, { collectionSlug: this.collectionSlug })
            .mapStream((data: any) => data.collection?.productVariants.items ?? [])
            .subscribe(items => {
                this.allVariants = items;

                // Create filtered stream
                this.filteredVariants$ = this.searchTerm$.pipe(
                    startWith(''),
                    debounceTime(200),
                    distinctUntilChanged(),
                    map(term =>
                        term
                            ? this.allVariants.filter(variant =>
                                  variant.name.toLowerCase().includes(term.toLowerCase()),
                              )
                            : this.allVariants,
                    ),
                );

                // Trigger initial filtering
                this.searchTerm$.next('');
            });
    }

    selectProductVariant() {
        this.modalService
            .fromComponent(RelationSelectorDialogComponent, {
                size: 'md',
                closable: true,
                locals: {
                    title: 'Select Product Variant',
                    selectorTemplate: this.template,
                },
            })
            .subscribe(result => {
                if (result) {
                    this.formControl.setValue(result);
                    this.formControl.markAsDirty();
                }
            });
    }

    remove() {
        this.formControl.setValue(null);
        this.formControl.markAsDirty();
    }
}
